<template>
  <div v-if="isVisible" class="filter-overlay">
    <div class="filter-overlay-backdrop" @click="close"></div>
    <div class="filter-overlay-content">
      <div class="filter-header">
        <h2>{{ title }} Filter Options :</h2>
      </div>

      <div class="filter-options">
        <!-- Filter options based on filter type -->
        <template v-if="filterType === 'mechanical'">
          <div class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Has Radio</label>
              <div class="hint-icon" title="Mechanical water meters require a clip-on radio module to transmit readings. This filter identifies meters with radio modules installed.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.hasRadio === 'yes' }]"
                  @click="filterOptions.hasRadio = filterOptions.hasRadio === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasRadio === 'no' }]"
                  @click="filterOptions.hasRadio = filterOptions.hasRadio === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasRadio === '' }]"
                  @click="filterOptions.hasRadio = ''"
              >
                Any
              </button>
            </div>
          </div>

          <div class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Attached to MPID</label>
              <div class="hint-icon" title="Filter meters based on their association with metering point id.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.hasMpid === 'yes' }]"
                  @click="filterOptions.hasMpid = filterOptions.hasMpid === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasMpid === 'no' }]"
                  @click="filterOptions.hasMpid = filterOptions.hasMpid === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasMpid === '' }]"
                  @click="filterOptions.hasMpid = ''"
              >
                Any
              </button>
            </div>
          </div>

          <div class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Has Readings</label>
              <div class="hint-icon" title="Filter meters based on whether they have transmitted consumption data at least once since installation.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.hasReadings === 'yes' }]"
                  @click="filterOptions.hasReadings = filterOptions.hasReadings === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasReadings === 'no' }]"
                  @click="filterOptions.hasReadings = filterOptions.hasReadings === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasReadings === '' }]"
                  @click="filterOptions.hasReadings = ''"
              >
                Any
              </button>
            </div>
          </div>
        </template>

        <!-- DLMS filter options -->
        <template v-else-if="filterType === 'dlms'">
          <div class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Is Integrated</label>
              <div class="hint-icon" title="Distinguish between integrated smart meters and non-integrated clip-on modules for mechanical meters.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.isIntegrated === 'yes' }]"
                  @click="filterOptions.isIntegrated = filterOptions.isIntegrated === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.isIntegrated === 'no' }]"
                  @click="filterOptions.isIntegrated = filterOptions.isIntegrated === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.isIntegrated === '' }]"
                  @click="filterOptions.isIntegrated = ''"
              >
                Any
              </button>
            </div>
          </div>

          <!-- Show "Attached to Meter" option only when "Is Integrated" is set to "no" -->
          <div v-if="filterOptions.isIntegrated === 'no'" class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Attached to Meter</label>
              <div class="hint-icon" title="Filter clip-on devices based on whether they are currently attached to a mechanical meter.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.attachedToMeter === 'yes' }]"
                  @click="filterOptions.attachedToMeter = filterOptions.attachedToMeter === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.attachedToMeter === 'no' }]"
                  @click="filterOptions.attachedToMeter = filterOptions.attachedToMeter === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.attachedToMeter === '' }]"
                  @click="filterOptions.attachedToMeter = ''"
              >
                Any
              </button>
            </div>
          </div>

          <div v-if="filterOptions.isIntegrated !== 'no'" class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Attached to MPID</label>
              <div class="hint-icon" title="Filter meters based on their association with metering point id.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.hasMpid === 'yes' }]"
                  @click="filterOptions.hasMpid = filterOptions.hasMpid === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasMpid === 'no' }]"
                  @click="filterOptions.hasMpid = filterOptions.hasMpid === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasMpid === '' }]"
                  @click="filterOptions.hasMpid = ''"
              >
                Any
              </button>
            </div>
          </div>

          <div class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Has Readings</label>
              <div class="hint-icon" title="Filter meters based on whether they have transmitted consumption data at least once since installation.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.hasReadings === 'yes' }]"
                  @click="filterOptions.hasReadings = filterOptions.hasReadings === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasReadings === 'no' }]"
                  @click="filterOptions.hasReadings = filterOptions.hasReadings === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasReadings === '' }]"
                  @click="filterOptions.hasReadings = ''"
              >
                Any
              </button>
            </div>
          </div>
        </template>

        <!-- CTR filter options (same as DLMS) -->
        <template v-else-if="filterType === 'ctr'">
          <div class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Is Integrated</label>
              <div class="hint-icon" title="Distinguish between integrated smart meters and non-integrated clip-on modules for mechanical meters.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.isIntegrated === 'yes' }]"
                  @click="filterOptions.isIntegrated = filterOptions.isIntegrated === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.isIntegrated === 'no' }]"
                  @click="filterOptions.isIntegrated = filterOptions.isIntegrated === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.isIntegrated === '' }]"
                  @click="filterOptions.isIntegrated = ''"
              >
                Any
              </button>
            </div>
          </div>

          <!-- Show "Attached to Meter" option only when "Is Integrated" is set to "no" -->
          <div v-if="filterOptions.isIntegrated === 'no'" class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Attached to Meter</label>
              <div class="hint-icon" title="Filter clip-on devices based on whether they are currently attached to a mechanical meter.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.attachedToMeter === 'yes' }]"
                  @click="filterOptions.attachedToMeter = filterOptions.attachedToMeter === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.attachedToMeter === 'no' }]"
                  @click="filterOptions.attachedToMeter = filterOptions.attachedToMeter === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.attachedToMeter === '' }]"
                  @click="filterOptions.attachedToMeter = ''"
              >
                Any
              </button>
            </div>
          </div>

          <div v-if="filterOptions.isIntegrated !== 'no'" class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Attached to MPID</label>
              <div class="hint-icon" title="Filter meters based on their association with metering point id.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.hasMpid === 'yes' }]"
                  @click="filterOptions.hasMpid = filterOptions.hasMpid === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasMpid === 'no' }]"
                  @click="filterOptions.hasMpid = filterOptions.hasMpid === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasMpid === '' }]"
                  @click="filterOptions.hasMpid = ''"
              >
                Any
              </button>
            </div>
          </div>

          <div class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Has Readings</label>
              <div class="hint-icon" title="Filter meters based on whether they have transmitted consumption data at least once since installation.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.hasReadings === 'yes' }]"
                  @click="filterOptions.hasReadings = filterOptions.hasReadings === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasReadings === 'no' }]"
                  @click="filterOptions.hasReadings = filterOptions.hasReadings === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasReadings === '' }]"
                  @click="filterOptions.hasReadings = ''"
              >
                Any
              </button>
            </div>
          </div>
        </template>

        <!-- LORA & WBUS filter options (same as DLMS) -->
        <template v-else-if="filterType === 'lora_wbus'">
          <div class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Is Integrated</label>
              <div class="hint-icon" title="Distinguish between integrated smart meters and non-integrated clip-on modules for mechanical meters.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.isIntegrated === 'yes' }]"
                  @click="filterOptions.isIntegrated = filterOptions.isIntegrated === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.isIntegrated === 'no' }]"
                  @click="filterOptions.isIntegrated = filterOptions.isIntegrated === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.isIntegrated === '' }]"
                  @click="filterOptions.isIntegrated = ''"
              >
                Any
              </button>
            </div>
          </div>

          <!-- Show "Attached to Meter" option only when "Is Integrated" is set to "no" -->
          <div v-if="filterOptions.isIntegrated === 'no'" class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Attached to Meter</label>
              <div class="hint-icon" title="Filter clip-on devices based on whether they are currently attached to a mechanical meter.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.attachedToMeter === 'yes' }]"
                  @click="filterOptions.attachedToMeter = filterOptions.attachedToMeter === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.attachedToMeter === 'no' }]"
                  @click="filterOptions.attachedToMeter = filterOptions.attachedToMeter === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.attachedToMeter === '' }]"
                  @click="filterOptions.attachedToMeter = ''"
              >
                Any
              </button>
            </div>
          </div>

          <div v-if="filterOptions.isIntegrated !== 'no'" class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Attached to MPID</label>
              <div class="hint-icon" title="Filter meters based on their association with metering point id.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.hasMpid === 'yes' }]"
                  @click="filterOptions.hasMpid = filterOptions.hasMpid === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasMpid === 'no' }]"
                  @click="filterOptions.hasMpid = filterOptions.hasMpid === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasMpid === '' }]"
                  @click="filterOptions.hasMpid = ''"
              >
                Any
              </button>
            </div>
          </div>

          <div class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Has Readings</label>
              <div class="hint-icon" title="Filter meters based on whether they have transmitted consumption data at least once since installation.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.hasReadings === 'yes' }]"
                  @click="filterOptions.hasReadings = filterOptions.hasReadings === 'yes' ? '' : 'yes'"
              >
                Yes
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasReadings === 'no' }]"
                  @click="filterOptions.hasReadings = filterOptions.hasReadings === 'no' ? '' : 'no'"
              >
                No
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.hasReadings === '' }]"
                  @click="filterOptions.hasReadings = ''"
              >
                Any
              </button>
            </div>
          </div>

          <!-- New Device Subtype filter for LORA & WBUS -->
          <div class="filter-option-group">
            <div class="filter-label-container">
              <label class="filter-label">Device Subtype</label>
              <div class="hint-icon" title="Filter by specific device subtype within LORA & WBUS category.">?</div>
            </div>
            <div class="toggle-selection-wrapper">
              <button
                  :class="['toggle-option', { active: filterOptions.deviceSubtype === 'lora' }]"
                  @click="filterOptions.deviceSubtype = filterOptions.deviceSubtype === 'lora' ? '' : 'lora'"
              >
                LORA
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.deviceSubtype === 'wbus' }]"
                  @click="filterOptions.deviceSubtype = filterOptions.deviceSubtype === 'wbus' ? '' : 'wbus'"
              >
                WBUS
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.deviceSubtype === 'both' }]"
                  @click="filterOptions.deviceSubtype = filterOptions.deviceSubtype === 'both' ? '' : 'both'"
              >
                LORA & WBUS
              </button>
              <button
                  :class="['toggle-option', { active: filterOptions.deviceSubtype === '' }]"
                  @click="filterOptions.deviceSubtype = ''"
              >
                Any
              </button>
            </div>
          </div>
        </template>
      </div>

      <div class="filter-actions">
        <button class="action-button" @click="close">Cancel</button>
        <button class="action-button" @click="applyFilters">Apply Filters</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FilterOverlay',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    filterType: {
      type: String,
      default: ''
    },
    initialFilterOptions: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      filterOptions: {
        // Mechanical filters
        hasRadio: '',
        hasMpid: '',
        hasReadings: '',

        // DLMS, CTR and LORA & WBUS filters
        isIntegrated: '',
        attachedToMeter: '',

        // LORA & WBUS specific filter
        deviceSubtype: '',
      },
    };
  },
  computed: {
    title() {
      // Capitalize first letter and handle special cases
      if (this.filterType === 'lora_wbus') {
        return 'Lora & Wbus';
      }
      if (this.filterType === 'ctr') {
        return 'CTR';
      }
      return this.filterType.charAt(0).toUpperCase() + this.filterType.slice(1);
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden'; // Prevent scrolling when overlay is open

        // Initialize filter options
        this.resetFilterOptions();

        // Copy initial filter options if available
        if (this.initialFilterOptions && Object.keys(this.initialFilterOptions).length > 0) {
          // Handle other fields
          if (this.initialFilterOptions.hasRadio !== undefined) {
            this.filterOptions.hasRadio = this.initialFilterOptions.hasRadio;
          }

          if (this.initialFilterOptions.hasMpid !== undefined) {
            this.filterOptions.hasMpid = this.initialFilterOptions.hasMpid;
          }

          if (this.initialFilterOptions.hasReadings !== undefined) {
            this.filterOptions.hasReadings = this.initialFilterOptions.hasReadings;
          }

          if (this.initialFilterOptions.isIntegrated !== undefined) {
            this.filterOptions.isIntegrated = this.initialFilterOptions.isIntegrated;
          }

          if (this.initialFilterOptions.attachedToMeter !== undefined) {
            this.filterOptions.attachedToMeter = this.initialFilterOptions.attachedToMeter;
          }

          if (this.initialFilterOptions.deviceSubtype !== undefined) {
            this.filterOptions.deviceSubtype = this.initialFilterOptions.deviceSubtype;
          }
        }
      } else {
        document.body.style.overflow = ''; // Restore scrolling when overlay is closed
      }
    },

    // Reset attachedToMeter when isIntegrated changes and it's not 'no'
    'filterOptions.isIntegrated'(newVal) {
      if (newVal !== 'no') {
        this.filterOptions.attachedToMeter = '';
      }
    }
  },
  methods: {
    resetFilterOptions() {
      // Reset all filter options to defaults
      this.filterOptions = {
        hasRadio: '',
        hasMpid: '',
        hasReadings: '',
        isIntegrated: '',
        attachedToMeter: '',
        deviceSubtype: '',
      };
    },

    close() {
      this.$emit('close');
    },

    applyFilters() {
      // Clean up attachedToMeter if isIntegrated is not 'no'
      if (this.filterOptions.isIntegrated !== 'no') {
        this.filterOptions.attachedToMeter = '';
      }

      this.$emit('apply', {
        filterType: this.filterType,
        options: this.filterOptions
      });

      this.close();
    }
  }
};
</script>

<style scoped>
/* Overlay */
.filter-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.filter-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32, 32, 32, 0.1) 0%, rgba(16, 16, 16, 0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.filter-overlay-content {
  position: relative;
  width: 500px;
  max-width: 90%;
  max-height: 90vh;
  border-radius: 8px;
  padding: 24px;
  color: white;
  overflow-y: auto;
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
}

/* Header */
.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 16px;
}

.filter-header h2 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

/* Filter options */
.filter-options {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
}

.filter-option-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.9;
}

.hint-icon {

  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: rgba(79, 195, 247, 0.3);
  color: #4fc3f7;
  font-size: 7px;
  font-weight: bold;
  cursor: help;
  transition: background-color 0.2s, color 0.2s;
}

.hint-icon:hover {
  background-color: rgba(79, 195, 247, 0.6);
  color: white;
}

/* Toggle selection style for all filter options */
.toggle-selection-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 8px;
}

.toggle-option {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;
  padding: 0;
  opacity: 0.4;
  transition: opacity 0.2s;
}

.toggle-option:hover,
.toggle-option.active {
  opacity: 1;
}

.toggle-option.active {
  position: relative;
}

.toggle-option.active::after {
  content: '';
  position: absolute;
  bottom: -5px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #4fc3f7;
  opacity: 0.7;
}

/* Action buttons - matching style from devices page */
.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 16px;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>